/**
 * 通话时长口径（全站面向用户的「通话时长」唯一出口）。
 *
 * 为什么需要它：FreeSWITCH 的 `duration` 是**通道生命周期**——呼出从拨号（通道创建）起算、
 * 呼入从进线起算，含振铃/IVR/排队；录音则是从接通（answer）开始录。两者不同轴，
 * 直接拿 `duration` 当「通话时长」就会出现「详情显示 1 分 2 秒、录音只有 56 秒」的对不上。
 * `billsec`（应答→挂断）才是通话时长，也是报表/BI/满意度/导出已统一采用的口径
 * （`AVG(billsec)`、`SUM(billsec)`）；本文件把这套口径收在一处，避免各页各写一遍。
 */
import dayjs from 'dayjs';

/** 通话时长计算所需的 CDR 字段（结构化入参，不依赖具体 Cdr 类型） */
export interface CallTimeFields {
  /** 计费时长（应答→挂断，秒） */
  billsec?: number | null;
  /** 通道时长（拨号/进线→挂断，秒），含振铃；仅作缺失兜底 */
  duration?: number | null;
  start_time?: string | null;
  answer_time?: string | null;
}

const sec = (v: unknown): number => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * 通话时长（秒）= 接通→挂断，即 `billsec`。
 * 未接通（无 answer_time）没有通话时长，返回 0，由调用方决定显示「-」；
 * `billsec` 缺失但已接通的历史行回落通道时长，避免显示成 0 秒。
 */
export function talkSeconds(cdr?: CallTimeFields | null): number {
  if (!cdr) return 0;
  const billsec = sec(cdr.billsec);
  if (billsec > 0) return billsec;
  return cdr.answer_time ? sec(cdr.duration) : 0;
}

/**
 * 等待接通时长（秒）= 开始（拨号/进线）→接通，即「通话时长之外」的那段：
 * 呼出为振铃、呼入为 IVR/排队+振铃。未接通时整段通道时长都是等待，按 `duration` 返回。
 */
export function waitSeconds(cdr?: CallTimeFields | null): number {
  if (!cdr) return 0;
  const start = cdr.start_time ? dayjs(cdr.start_time) : null;
  const answer = cdr.answer_time ? dayjs(cdr.answer_time) : null;
  if (start?.isValid() && answer?.isValid()) {
    const diff = Math.floor(answer.diff(start, 'second', true));
    return diff > 0 ? diff : 0;
  }
  return answer ? 0 : sec(cdr.duration);
}
